// Firebase Auth is used only to prove that an email address belongs to the person signing up: Firebase emails the link and
// tracks whether it was opened. Lyra accounts, passwords and sessions stay Lyra's own (server /auth/*).
import { initializeApp } from "firebase/app";
import {
  createUserWithEmailAndPassword, getAuth, sendEmailVerification, signInWithEmailAndPassword, signOut,
} from "firebase/auth";

const config = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const auth = config.apiKey && config.authDomain && config.projectId && config.appId ? getAuth(initializeApp(config)) : null;

export const firebaseConfigured = !!auth;

const code = (e) => String((e && (e.code || e.message)) || "").toLowerCase();

export function friendlyFirebaseError(e) {
  const c = code(e);
  if (c.includes("too-many-requests")) return "Too many attempts. Please wait a few minutes and try again.";
  if (c.includes("email-already-in-use") || c.includes("invalid-credential") || c.includes("wrong-password"))
    return "This email already has a verification profile with a different password. Use the password you first signed up with.";
  if (c.includes("network")) return "Network error. Please try again.";
  return "Could not complete email verification. Please try again.";
}

/** Signs into Firebase with the Lyra credentials (creating the Firebase user the first time), so a verification email can be sent. */
async function ensureFirebaseUser(email, password) {
  if (!auth) throw new Error("Email verification is not configured.");
  try {
    return (await signInWithEmailAndPassword(auth, email, password)).user;
  } catch (e) {
    // Firebase answers "invalid-credential" for both "no such user" and "wrong password": try to create, and let
    // email-already-in-use tell the two apart.
    return (await createUserWithEmailAndPassword(auth, email, password)).user;
  }
}

/** Emails the verification link. Resolves { verified: true } when Firebase already considers the address verified. */
export async function sendVerification(email, password) {
  const user = await ensureFirebaseUser(email, password);
  if (user.emailVerified) return { verified: true };
  await sendEmailVerification(user);
  return { verified: false };
}

/** Re-sends the link for the Firebase user already signed in by sendVerification. */
export async function resendVerification() {
  if (!auth || !auth.currentUser) throw new Error("Start again from sign in.");
  await sendEmailVerification(auth.currentUser);
}

/** The current user's ID token once their email shows as verified, otherwise null. */
export async function verifiedIdToken() {
  if (!auth || !auth.currentUser) return null;
  await auth.currentUser.reload();
  if (!auth.currentUser.emailVerified) return null;
  return auth.currentUser.getIdToken(true);
}

export async function clearFirebaseSession() {
  if (auth) { try { await signOut(auth); } catch (e) { /* nothing to clear */ } }
}
